/**
 * Muze hero carousel.
 *
 * Crossfading slides with autoplay, dots, arrows, swipe and keyboard control.
 * Inactive slides are made `inert` so their links stay out of the tab order and
 * the accessibility tree.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 40;

  function MuzeHero(root) {
    this.root = root;
    this.track = root.querySelector('[data-mh-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-mh-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-mh-dot]'));
    this.status = root.querySelector('[data-mh-status]');

    this.index = Math.max(0, this.slides.findIndex(function (s) {
      return s.classList.contains('muze-hero__slide--active');
    }));

    this.interval = parseInt(root.dataset.mhInterval, 10) || 0;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.interval > 0 && !this.reduced && this.slides.length > 1;
    this.timer = null;
    this.paused = false;
    this.pointerX = null;

    if (!this.autoplay) root.classList.add('muze-hero--static');

    this.applyState();

    if (this.slides.length > 1) {
      this.bind();
      this.start();
    }
  }

  MuzeHero.prototype.bind = function () {
    var self = this;

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.goTo(parseInt(dot.dataset.mhDot, 10), true);
      });
    });

    var prev = this.root.querySelector('[data-mh-prev]');
    var next = this.root.querySelector('[data-mh-next]');
    if (prev) prev.addEventListener('click', function () { self.goTo(self.index - 1, true); });
    if (next) next.addEventListener('click', function () { self.goTo(self.index + 1, true); });

    // Pause while the visitor is reading or interacting.
    this.root.addEventListener('mouseenter', function () { self.pause(); });
    this.root.addEventListener('mouseleave', function () { self.resume(); });
    this.root.addEventListener('focusin', function () { self.pause(); });
    this.root.addEventListener('focusout', function (e) {
      if (!self.root.contains(e.relatedTarget)) self.resume();
    });

    this._onVisibility = function () {
      if (document.hidden) self.pause();
      else self.resume();
    };
    document.addEventListener('visibilitychange', this._onVisibility);

    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.goTo(self.index - 1, true); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self.goTo(self.index + 1, true); }
    });

    // Swipe. Vertical drags are ignored so page scrolling still works.
    this.track.addEventListener('pointerdown', function (e) {
      self.pointerX = e.clientX;
      self.pointerY = e.clientY;
    });
    this.track.addEventListener('pointerup', function (e) {
      if (self.pointerX === null) return;
      var dx = e.clientX - self.pointerX;
      var dy = e.clientY - self.pointerY;
      self.pointerX = null;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        self.goTo(self.index + (dx < 0 ? 1 : -1), true);
      }
    });
    this.track.addEventListener('pointercancel', function () { self.pointerX = null; });
  };

  MuzeHero.prototype.applyState = function () {
    var self = this;

    this.slides.forEach(function (slide, i) {
      var active = i === self.index;
      slide.classList.toggle('muze-hero__slide--active', active);
      if (active) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });

    this.dots.forEach(function (dot, i) {
      var active = i === self.index;
      dot.classList.toggle('muze-hero__dot--active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
      // Restart the progress fill on the newly active dot.
      var fill = dot.querySelector('.muze-hero__dot-fill');
      if (fill && active) {
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }
    });

    if (this.status) {
      this.status.textContent = 'Slide ' + (this.index + 1) + ' of ' + this.slides.length;
    }
  };

  MuzeHero.prototype.goTo = function (i, fromUser) {
    var count = this.slides.length;
    this.index = ((i % count) + count) % count;
    this.applyState();
    if (fromUser) this.restart();
  };

  MuzeHero.prototype.start = function () {
    if (!this.autoplay || this.paused) return;
    this.stop();
    var self = this;
    this.timer = setInterval(function () { self.goTo(self.index + 1, false); }, this.interval);
  };

  MuzeHero.prototype.stop = function () {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  };

  MuzeHero.prototype.restart = function () {
    this.stop();
    this.start();
  };

  MuzeHero.prototype.pause = function () {
    this.paused = true;
    this.root.classList.add('muze-hero--paused');
    this.stop();
  };

  MuzeHero.prototype.resume = function () {
    if (!this.paused) return;
    this.paused = false;
    this.root.classList.remove('muze-hero--paused');
    this.start();
  };

  MuzeHero.prototype.destroy = function () {
    this.stop();
    document.removeEventListener('visibilitychange', this._onVisibility);
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-muze-hero]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.muzeHero) root.muzeHero.destroy();
      root.muzeHero = new MuzeHero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-muze-hero]');
    if (root && root.muzeHero) root.muzeHero.destroy();
  });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest('[data-muze-hero]');
    if (!root || !root.muzeHero) return;
    var i = root.muzeHero.slides.indexOf(e.target);
    if (i > -1) root.muzeHero.goTo(i, true);
    root.muzeHero.pause();
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var root = e.target.closest('[data-muze-hero]');
    if (root && root.muzeHero) root.muzeHero.resume();
  });
})();
